import {
  ActionStore,
  createAction, createMutation, Entity, mutate,
  on,
  RemoteObjectStore, ResponseResult,
  SOFTLINE_SERVICE_UUID,
  StoreBuilder,
  SubscriptionStore,
  DefaultStore
} from "@softline/core";
import { Arbeit, Edelmetall, Material, Produktkalkulation } from "../types/produktkalkulation";
import { lastValueFrom } from "rxjs";
import { ProduktkalkulationService } from "../services/produktkalkulation.service";


class ProduktkalkulationStoreExtension {
  static mutations = {
    editEdelmetall: createMutation<RemoteObjectStore.State<Produktkalkulation>, Edelmetall>('editEdelmetall'),
    addMaterial: createMutation<RemoteObjectStore.State<Produktkalkulation>, Material>('addMaterial'),
    editMaterial: createMutation<RemoteObjectStore.State<Produktkalkulation>, { old: Material, new: Material }>('editMaterial'),
    deleteMaterial: createMutation<RemoteObjectStore.State<Produktkalkulation>, Material>('deleteMaterial'),
    addArbeit: createMutation<RemoteObjectStore.State<Produktkalkulation>, Arbeit>('addArbeit'),
    editArbeit: createMutation<RemoteObjectStore.State<Produktkalkulation>, { old: Arbeit, new: Arbeit }>('editArbeit'),
    deleteArbeit: createMutation<RemoteObjectStore.State<Produktkalkulation>, Arbeit>('deleteArbeit'),
  }

  static actions = {
    recalculate: createAction<RemoteObjectStore.State<Produktkalkulation>, undefined, Produktkalkulation>('recalculate'),
    save: createAction<RemoteObjectStore.State<Produktkalkulation>, undefined>('save'),
    saveStueckliste: createAction<RemoteObjectStore.State<Produktkalkulation>, undefined>('saveStueckliste'),
  }

  static feature = {
    mutations: [
      mutate(ProduktkalkulationStoreExtension.mutations.editEdelmetall, ({state, params}) => {
        if(!state.data)
          throw new Error('[ProduktkalkulationStoreExtension] No data!')
        const edelmetalle = [...state.data.edelmetalle]
        const index = edelmetalle.findIndex(
          (o: Edelmetall) => o.begriff === params.begriff);
        edelmetalle.splice(index, 1, params);
        return {...state, data: {...state.data, edelmetalle}}
      }),
      mutate(ProduktkalkulationStoreExtension.mutations.addMaterial, ({state, params}) => {
        if(!state.data)
          throw new Error('[ProduktkalkulationStoreExtension] No data!')
        const materialien = [...state.data.materialien, params]
        return {...state, data: {...state.data, materialien}}
      }),
      mutate(ProduktkalkulationStoreExtension.mutations.editMaterial, ({state, params}) => {
        if(!state.data)
          throw new Error('[ProduktkalkulationStoreExtension] No data!')
        const materialien = [...state.data.materialien]
        const index = materialien.indexOf(params.old);
        materialien.splice(index, 1, params.new);
        return {...state, data: {...state.data, materialien}}
      }),
      mutate(ProduktkalkulationStoreExtension.mutations.deleteMaterial, ({state, params}) => {
        if(!state.data)
          throw new Error('[ProduktkalkulationStoreExtension] No data!')
        const materialien = [...state.data.materialien]
        const index = materialien.indexOf(params);
        console.log(index, materialien)
        materialien.splice(index, 1);
        console.log(index, materialien)
        return {...state, data: {...state.data, materialien}}
      }),
      mutate(ProduktkalkulationStoreExtension.mutations.addArbeit, ({state, params}) => {
        if(!state.data)
          throw new Error('[ProduktkalkulationStoreExtension] No data!')
        const arbeit = [...state.data.arbeit, params]
        return {...state, data: {...state.data, arbeit}}
      }),
      mutate(ProduktkalkulationStoreExtension.mutations.editArbeit, ({state, params}) => {
        if(!state.data)
          throw new Error('[ProduktkalkulationStoreExtension] No data!')
        const arbeit = [...state.data.arbeit]
        const index = arbeit.indexOf(params.old);
        arbeit.splice(index, 1, params.new);
        return {...state, data: {...state.data, arbeit}}
      }),
      mutate(ProduktkalkulationStoreExtension.mutations.deleteArbeit, ({state, params}) => {
        if(!state.data)
          throw new Error('[ProduktkalkulationStoreExtension] No data!')
        const arbeit = [...state.data.arbeit]
        const index = arbeit.indexOf(params);
        arbeit.splice(index, 1);
        return {...state, data: {...state.data, arbeit}}
      })
    ],
    actions: [
      on(ProduktkalkulationStoreExtension.actions.recalculate, async ({featureName, commit, store, injector, state, params}) => {
        const service = injector.get(ProduktkalkulationService);
        const token = injector.get(SOFTLINE_SERVICE_UUID)();
        const produktkalkulation = state.data;
        if(!produktkalkulation)
          throw new Error('[ProduktkalkulationStoreExtension] No data!')
        const subscriptionState$ = SubscriptionStore.handleSubscriptionState(
          service.recalculate(produktkalkulation),
          featureName,
          commit,
          token
        );
        const result = await lastValueFrom(
          ActionStore.handleObservableActionState(
            subscriptionState$,
            featureName,
            commit,
            ProduktkalkulationStoreExtension.actions.recalculate.name,
            token
          )
        );
        commit(featureName, RemoteObjectStore.mutations.set, result);
        return result;
      }),
      on(ProduktkalkulationStoreExtension.actions.save, async ({featureName, commit, store, injector, state, params}) => {
        const service = injector.get(ProduktkalkulationService);
        const token = injector.get(SOFTLINE_SERVICE_UUID)();
        const produktkalkulation = state.data;
        if(!produktkalkulation)
          throw new Error('[ProduktkalkulationStoreExtension] No data!')
        const subscriptionState$ = SubscriptionStore.handleSubscriptionState(
          service.save(produktkalkulation),
          featureName,
          commit,
          token
        );
        const result = await lastValueFrom(
          ActionStore.handleObservableActionState(
            subscriptionState$,
            featureName,
            commit,
            ProduktkalkulationStoreExtension.actions.recalculate.name,
            token
          )
        );
        commit(featureName, RemoteObjectStore.mutations.set, undefined);
      }),
      on(ProduktkalkulationStoreExtension.actions.saveStueckliste, async ({featureName, commit, store, injector, state, params}) => {
        const service = injector.get(ProduktkalkulationService);
        const token = injector.get(SOFTLINE_SERVICE_UUID)();
        const produktkalkulation = state.data;

        if (!produktkalkulation)
          throw new Error('[ProduktkalkulationStoreExtension] No data!');

        const subscriptionState$ = SubscriptionStore.handleSubscriptionState(
          service.saveStueckliste(produktkalkulation),
          featureName,
          commit,
          token
        );

        const result = await lastValueFrom(
          ActionStore.handleObservableActionState(
            subscriptionState$,
            featureName,
            commit,
            ProduktkalkulationStoreExtension.actions.recalculate.name,
            token
          )
        );
      })
    ]
  }
}

export type ProduktkalkulationInputTemplate = {
  idarteh: number | null;
  idartstamm: number | null;
  idkunde: number | null;
  artehBean: { id: number; arteh: string };
}

export const ProduktkalkulationStore = new StoreBuilder(RemoteObjectStore.create<Produktkalkulation>())
  .add(ProduktkalkulationStoreExtension)
  .add(DefaultStore.create<ResponseResult<ProduktkalkulationInputTemplate> & Entity>())
  .value;
